import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditableText } from '@/core/EditableText.atom';
import { FormError } from '@/core/FormError.atom';
import { DateTimeEntry } from '@/datetime/DateTimeEntry.atom';
import { STRING_UOM } from '@/main/app.constants';
import { triggerManualSignalPreview } from '@/tools/manualSignal/manualSignal.actions';
import { SAMPLE_VALUE_ERROR } from '@/tools/manualSignal/manualSignal.constants';
import {
  useManualSignalActions,
  useManualSignalSamples,
  useManualSignalUom,
} from '@/tools/manualSignal/manualSignal.store';
import { base64guid } from '@/utilities/guid.utilities';
import { Button } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';

interface SampleInputProps {
  testId: string;
  sample?: { dateTime: any; value: any; id?: string };
  timezone: {
    name: string;
    displayName: string;
    offset: string;
    offsetMinutes: number;
  };
}

export const SampleInput = ({ sample, timezone, testId }: SampleInputProps) => {
  const { t } = useTranslation();

  const samples = useManualSignalSamples();
  const isStringUom = useManualSignalUom() === STRING_UOM;
  const isStringSignal = isStringUom || Number.isNaN(Number(samples[0]?.value));
  const { addSample, updateSample, setEditingSampleId } = useManualSignalActions();
  const [dateTime, setDateTime] = useState<moment.Moment>(
    sample?.dateTime ? moment.utc(sample.dateTime) : moment.utc().startOf('minute'),
  );
  const [value, setValue] = useState(sample?.value ?? '');
  const [valueError, setValueError] = useState<string | undefined>(undefined);
  const [dateError, setDateError] = useState(false);

  useEffect(() => {
    const newDateTimeValue = dateTime.valueOf();
    const sampleId = sample?.id;
    const hasError = _.some(samples, (s) => s.dateTime === newDateTimeValue && s.id !== sampleId);
    setDateError(hasError && !_.isEmpty(value));
  }, [samples, dateTime, value]);

  useEffect(() => {
    const hasValueTypeError = !isStringSignal && _.isNaN(_.toNumber(value));
    setValueError(hasValueTypeError ? SAMPLE_VALUE_ERROR.INVALID_TYPE : undefined);
  }, [value, isStringSignal, samples, isStringUom]);

  const resetState = () => {
    setValue('');
    setDateTime(moment.utc().startOf('minute'));
    setValueError(undefined);
    setDateError(false);
  };

  const addNewSample = () => {
    if (_.isEmpty(value)) {
      setValueError(SAMPLE_VALUE_ERROR.EMPTY);
      return;
    }
    if (dateError || !_.isUndefined(valueError)) return;

    addSample({ id: base64guid(), dateTime: dateTime.valueOf(), value });
    triggerManualSignalPreview();
    resetState();
  };

  const saveEditedSample = () => {
    if (dateError || !_.isUndefined(valueError) || !sample) return;

    updateSample({ id: sample.id, dateTime: dateTime.valueOf(), value });
    setEditingSampleId(undefined);
    triggerManualSignalPreview();
    resetState();
  };

  const handleDateTimeUpdate = (date: moment.Moment | undefined) => {
    if (date) {
      setDateTime(date);
    }
  };

  return (
    <div {...stylex.props(styles.sampleEditor)} data-testid={testId}>
      <div {...stylex.props(styles.columnHeadings)}>
        <div {...stylex.props(styles.columnHeading)}>{t('MANUAL_SIGNAL.DATE_TIME')}</div>
        <div {...stylex.props(styles.columnHeading)}>{t('MANUAL_SIGNAL.VALUE')}</div>
      </div>
      <div {...stylex.props(styles.sampleFields)}>
        <div {...stylex.props(styles.sampleField)}>
          <DateTimeEntry
            testId="manualSignalDateTimeEntry"
            hideTimezone={true}
            trackCategory="Workbench_Tool"
            trackAction="Manual_Signal"
            trackInformation="Date time changed"
            datePickerXstyle={dateError ? styles.invalidValue : undefined}
            updateDate={handleDateTimeUpdate}
            timezone={timezone}
            date={dateTime}
            showSeconds={false}
          />
        </div>
        <div {...stylex.props(styles.sampleField)}>
          <div {...stylex.props(styles.sampleValue)}>
            <EditableText
              value={_.isEmpty(value) ? t('MANUAL_SIGNAL.VALUE_PLACEHOLDER') : value}
              inputXstyle={styles.valueInput}
              testId={sample?.id ? `sampleValue-${sample.id}` : 'newSampleValue'}
              maxDisplayChars={15}
              onUpdate={(newValue) => {
                setValueError(_.isEmpty(newValue) ? SAMPLE_VALUE_ERROR.EMPTY : undefined);
                setValue(newValue);
              }}
              textXstyle={[
                styles.editableValue,
                _.isEmpty(value) && styles.placeholder,
                !_.isUndefined(valueError) && styles.invalidValue,
              ]}
            />
          </div>
        </div>
      </div>

      {sample?.id ? (
        <div {...stylex.props(styles.editActions)}>
          <Button
            label={t('CANCEL')}
            xstyle={styles.cancelAction}
            onClick={() => {
              setEditingSampleId(undefined);
              triggerManualSignalPreview();
            }}
          />
          <Button
            testId="saveSampleButton"
            label={t('SAVE')}
            variant="theme"
            disabled={_.isEmpty(value)}
            xstyle={styles.submitAction}
            onClick={saveEditedSample}
          />
        </div>
      ) : (
        <div {...stylex.props(styles.addAction)}>
          <Button
            testId="addSampleButton"
            label={t('MANUAL_SIGNAL.ADD_SAMPLE')}
            xstyle={styles.submitAction}
            onClick={addNewSample}
          />
        </div>
      )}

      {dateError && (
        <FormError
          extraClassNames={stylex.props(styles.validationMessage).className}
          errorText="MANUAL_SIGNAL.DATE_TIME_ERROR"
        />
      )}

      {valueError === SAMPLE_VALUE_ERROR.INVALID_TYPE && (
        <FormError
          extraClassNames={stylex.props(styles.validationMessage).className}
          errorText="MANUAL_SIGNAL.VALUE_TYPE_ERROR"
        />
      )}
    </div>
  );
};

const styles = stylex.create({
  editableValue: { paddingTop: '0.0625rem', paddingBottom: '0.0625rem' },
  placeholder: { fontStyle: 'italic' },
  invalidValue: { color: { 'default': colors.borderInvalid, ':hover': colors.borderInvalid } },
  sampleEditor: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '0.625rem',
    paddingBottom: '0.625rem',
    borderRadius: 4,
    backgroundColor: colors.surfaceSubtle,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.sampleInputBorder,
  },
  columnHeadings: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    overflow: 'hidden',
    textAlign: 'center',
    paddingRight: '0.625rem',
    paddingLeft: '0.625rem',
    fontWeight: 700,
  },
  sampleFields: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    overflow: 'hidden',
    textAlign: 'center',
    paddingRight: '0.625rem',
    paddingLeft: '0.625rem',
    color: colors.legacyToolsPrimary,
  },
  columnHeading: { flex: '1 1 0%', overflow: 'hidden' },
  sampleField: { flex: '1 1 0%', overflow: 'hidden', alignSelf: 'center' },
  sampleValue: { whiteSpace: 'nowrap' },
  editActions: { display: 'flex', flexDirection: 'row', marginTop: '0.3125rem', justifyContent: 'center' },
  addAction: { textAlign: 'center', marginTop: '0.3125rem' },
  cancelAction: { display: 'inline-block', marginRight: 10 },
  submitAction: { display: 'inline-block' },
  valueInput: { textAlign: 'center' },
  validationMessage: { marginTop: 5, marginLeft: 10, marginRight: 1, marginBottom: 0 },
});
